import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmRecipientSearchField,
  kApdmRecipientSearchMaxLength,
} from '@euler/model/apdm';
import { Check, ChevronDown, Search, X } from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, TextInput, View } from 'react-native';

/** Each way to search, as the picker lists it, plate first. */
const kSearchFields: Array<{
  value: ApdmRecipientSearchField;
  label: string;
  placeholder: string;
}> = [
  {
    value: ApdmRecipientSearchField.Plate,
    label: '车牌号',
    placeholder: '搜索车牌号',
  },
  {
    value: ApdmRecipientSearchField.CustomerName,
    label: '客户姓名',
    placeholder: '搜索客户姓名',
  },
  {
    value: ApdmRecipientSearchField.Keyword,
    label: '关键词',
    placeholder: '搜索车牌、姓名、电话、车型等',
  },
];

/**
 * The customer list's search: the text, and what it matches, picked in a
 * sheet from the caret on the right.
 */
export const RecipientSearchBar = memo(
  ({
    text,
    field,
    onChangeText,
    onChangeField,
  }: {
    text: string;
    field: ApdmRecipientSearchField;
    onChangeText: (text: string) => void;
    onChangeField: (field: ApdmRecipientSearchField) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [picking, setPicking] = useState(false);
    const current =
      kSearchFields.find(option => option.value === field) ?? kSearchFields[0];
    return (
      <>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(8),
            height: rpx(40),
            paddingLeft: rpx(12),
            borderRadius: rpx(12),
            borderWidth: 1,
            borderColor: aiColors.border,
            backgroundColor: aiColors.card,
          }}
        >
          <Search size={rpx(16)} color={aiColors.mutedForeground} />
          <TextInput
            value={text}
            onChangeText={onChangeText}
            placeholder={current.placeholder}
            placeholderTextColor={aiColors.mutedForeground}
            maxLength={kApdmRecipientSearchMaxLength}
            autoCorrect={false}
            autoCapitalize={
              field === ApdmRecipientSearchField.Plate ? 'characters' : 'none'
            }
            returnKeyType="search"
            style={{
              flex: 1,
              minWidth: 0,
              height: '100%',
              padding: 0,
              fontSize: rpx(14),
              color: aiColors.foreground,
            }}
          />
          {text ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="清除"
              onPress={() => onChangeText('')}
              hitSlop={10}
              // The system clear button: a white cross on a grey disc.
              style={{
                width: rpx(17),
                height: rpx(17),
                borderRadius: rpx(8.5),
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#B0B0B6',
              }}
            >
              <X size={rpx(11)} color="#FFFFFF" strokeWidth={3} />
            </Pressable>
          ) : null}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`搜索方式：${current.label}`}
            onPress={() => setPicking(true)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(2),
              alignSelf: 'stretch',
              paddingLeft: rpx(10),
              paddingRight: rpx(12),
              borderLeftWidth: 1,
              borderLeftColor: aiColors.border,
            }}
          >
            <Typography.Text size={rpx(13)} color={aiColors.foreground}>
              {current.label}
            </Typography.Text>
            <ChevronDown size={rpx(14)} color={aiColors.mutedForeground} />
          </Pressable>
        </View>
        <BottomPanel
          visible={picking}
          onClose={() => setPicking(false)}
          fitContent
        >
          <View style={{ padding: rpx(20), gap: rpx(4) }}>
            <Typography.Text
              medium
              size={rpx(13)}
              color={aiColors.mutedForeground}
            >
              搜索方式
            </Typography.Text>
            {kSearchFields.map(option => {
              const selected = option.value === field;
              return (
                <Pressable
                  key={option.value}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  onPress={() => {
                    setPicking(false);
                    onChangeField(option.value);
                  }}
                  style={({ pressed }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: rpx(14),
                    opacity: pressed ? 0.6 : 1,
                  })}
                >
                  <Typography.Text
                    size={rpx(15)}
                    color={selected ? aiColors.primary : aiColors.foreground}
                  >
                    {option.label}
                  </Typography.Text>
                  {selected ? (
                    <Check size={rpx(18)} color={aiColors.primary} />
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </BottomPanel>
      </>
    );
  },
);
